import { useEffect, useState } from 'react';
import { Dropzone } from './App';

type Admissibility = { status: string; status_ru: string; grounds: string[] };

type Position = {
  num: number;
  name: string;
  qty: number | null;
  unit: string;
  category: 'exact' | 'analog' | 'review' | 'none';
  band: string;
  found: { name: string; code: string; price: number | null; sum: number | null } | null;
  explanation: string;
  admissibility: Admissibility | null;
};

type SamplePair = {
  id: string; title: string; desc: string; expect: string; spec: string; price: string;
};
type SamplesManifest = {
  files: { name: string; label: string; size: number }[];
  pairs: SamplePair[];
};

type RazborResponse = {
  spec_name: string;
  price_name: string;
  n_items: number;
  n_catalog: number;
  coverage: {
    total: number; exact: number; analog: number; review: number; none: number;
    forbidden: number; percent: number; verdict: string; blockers: string[];
  };
  money: { total: number; known_rows: number };
  positions: Position[];
  xlsx_base64: string;
  // Только у постоянных разборов примеров (/demo/?pair=<id>).
  pair?: { id: string; title: string; expect: string };
};

type Result = { data: RazborResponse; downloadUrl: string; permalink: string | null };

const CAT_LABEL: Record<Position['category'], string> = {
  exact: 'точно', analog: 'аналог', review: 'спорно', none: 'нет в прайсе',
};
const CAT_BADGE: Record<Position['category'], string> = {
  exact: 'bg-emerald-100 text-emerald-800',
  analog: 'bg-sky-100 text-sky-800',
  review: 'bg-amber-100 text-amber-800',
  none: 'bg-orange-100 text-orange-800',
};
const ADM_CHIP: Record<string, string> = {
  exact: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  equivalent_ok: 'bg-sky-50 text-sky-700 border-sky-200',
  equivalent_forbidden: 'bg-rose-50 text-rose-700 border-rose-200',
  undetermined: 'bg-slate-50 text-slate-600 border-slate-200',
};

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v);

// Постоянная ссылка на разбор примера: тот же путь (/demo/), параметр pair.
const pairUrl = (pairId: string) => {
  const u = new URL(window.location.href);
  u.search = `?pair=${encodeURIComponent(pairId)}`;
  u.hash = '';
  return u.toString();
};

const pairFromLocation = () => new URLSearchParams(window.location.search).get('pair');

// Метка источника в ссылке (?src=alyans-kabel) — кладём сами при рассылке,
// чтобы открытие атрибутировалось конкретному адресату ещё до ответа.
// Читаем один раз при загрузке страницы: клики по другим примерам внутри
// уже открытой демки — тоже вовлечённость того же посетителя, тег остаётся.
const srcFromLocation = () => new URLSearchParams(window.location.search).get('src');

const xlsxUrl = (b64: string) => {
  const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  return URL.createObjectURL(blob);
};

export function RazborFlow() {
  const [spec, setSpec] = useState<File | null>(null);
  const [price, setPrice] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingPair, setLoadingPair] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [samples, setSamples] = useState<SamplesManifest | null>(null);
  const [pickingSample, setPickingSample] = useState<string | null>(null);
  const [visitSrc] = useState<string | null>(srcFromLocation);

  useEffect(() => {
    fetch('/api/samples')
      .then((r) => (r.ok ? r.json() : null))
      .then(setSamples)
      .catch(() => setSamples(null));
  }, []);

  const openPair = async (pairId: string) => {
    setLoadingPair(pairId);
    setError(null);
    try {
      const qs = visitSrc ? `?src=${encodeURIComponent(visitSrc)}` : '';
      const res = await fetch(`/api/samples/pairs/${encodeURIComponent(pairId)}/razbor${qs}`);
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ detail: 'unknown error' }));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const data: RazborResponse = await res.json();
      setResult({ data, downloadUrl: xlsxUrl(data.xlsx_base64), permalink: pairUrl(pairId) });
    } catch (e: any) {
      setError(e.message || String(e));
      window.history.replaceState(null, '', window.location.pathname);
    } finally {
      setLoadingPair(null);
    }
  };

  // Открытие по постоянной ссылке /demo/?pair=<id> и переходы назад/вперёд.
  useEffect(() => {
    const sync = () => {
      const id = pairFromLocation();
      if (id) openPair(id);
      else setResult(null);
    };
    sync();
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  const showPair = (pairId: string) => {
    window.history.pushState(null, '', pairUrl(pairId));
    openPair(pairId);
  };

  const pickSample = async (pair: SamplePair) => {
    setPickingSample(pair.id);
    setError(null);
    try {
      const fetchAsFile = async (name: string) => {
        const r = await fetch(`/api/samples/${name}`);
        if (!r.ok) throw new Error(`не удалось получить пример ${name}`);
        return new File([await r.blob()], name);
      };
      setSpec(await fetchAsFile(pair.spec));
      setPrice(await fetchAsFile(pair.price));
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setPickingSample(null);
    }
  };

  const reset = () => {
    setSpec(null);
    setPrice(null);
    if (result) URL.revokeObjectURL(result.downloadUrl);
    setResult(null);
    setError(null);
    if (pairFromLocation()) window.history.pushState(null, '', window.location.pathname);
  };

  const submit = async () => {
    if (!spec || !price) return;
    setLoading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('spec', spec);
      fd.append('price', price);
      const res = await fetch('/api/razbor', { method: 'POST', body: fd });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ detail: 'unknown error' }));
        throw new Error(errBody.detail || `HTTP ${res.status}`);
      }
      const data: RazborResponse = await res.json();
      setResult({ data, downloadUrl: xlsxUrl(data.xlsx_base64), permalink: null });
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <RazborResult
        data={result.data}
        downloadUrl={result.downloadUrl}
        permalink={result.permalink}
        onReset={reset}
      />
    );
  }

  if (loadingPair) {
    const title = samples?.pairs.find((p) => p.id === loadingPair)?.title ?? 'пример';
    return (
      <div className="py-10 text-center text-slate-600">
        <div className="text-lg font-medium text-slate-900">Открываем разбор: {title}</div>
        <div className="text-sm mt-2">Разбор примера уже посчитан — сейчас загрузится.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">Спецификация / ТЗ</div>
          <Dropzone
            file={spec}
            onPick={setSpec}
            hint="xlsx, xls, docx, pdf (в т.ч. скан) или txt"
            accept=".xlsx,.xlsm,.xls,.docx,.pdf,.txt"
          />
        </div>
        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">Ваш прайс / каталог</div>
          <Dropzone
            file={price}
            onPick={setPrice}
            hint="xlsx, xls или выгрузка 1С (CommerceML: xml, zip)"
            accept=".xlsx,.xlsm,.xls,.xml,.zip"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-sm px-4 py-3">
          {error}
        </div>
      )}

      <div className="flex items-center gap-4">
        <button
          onClick={submit}
          disabled={!spec || !price || loading}
          className="bg-slate-900 text-white rounded-lg px-6 py-2.5 font-semibold disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {loading ? 'Разбираем…' : 'Разобрать спецификацию'}
        </button>
        {loading && (
          <span className="text-sm text-slate-500">
            Новый прайс сначала индексируется — для большого файла это заметное ожидание.
          </span>
        )}
      </div>

      {samples && samples.pairs.length > 0 && (
        <div className="pt-6 border-t border-slate-200">
          <div className="text-sm font-medium text-slate-700">
            Нет документов под рукой? Посмотрите готовые разборы реальных закупок
          </div>
          <div className="text-xs text-slate-500 mt-0.5 mb-4">
            Настоящие ТЗ с zakupki.gov.ru и публичные прайсы поставщиков. У каждого разбора постоянная ссылка.
          </div>
          <div className="grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
            {samples.pairs.map((pair) => {
              const { title, warning } = splitWarning(pair.title);
              const desc = splitWarning(pair.desc);
              // Предупреждение может стоять и в названии, и в описании — собираем в одну заметку.
              const note = [warning, desc.warning].filter(Boolean).join(' — ');
              return (
                <div key={pair.id} className="flex flex-col rounded-xl border border-slate-200 p-4">
                  <div className="break-anywhere text-sm font-semibold leading-snug text-slate-900">
                    {title}
                  </div>
                  {note && (
                    <div className="mt-2 flex gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs leading-snug text-amber-900">
                      <span aria-hidden className="shrink-0">⚠️</span>
                      <ClampedText text={note} threshold={140} clampClass="line-clamp-3" className="min-w-0 flex-1" />
                    </div>
                  )}
                  <div className="mt-2 flex-1 break-anywhere text-xs leading-snug text-slate-500">
                    {desc.title}
                  </div>
                  <div className="mt-2 break-anywhere text-xs font-semibold text-slate-700">
                    Вердикт: {pair.expect}
                  </div>
                  <a
                    href={pairUrl(pair.id)}
                    onClick={(e) => { e.preventDefault(); showPair(pair.id); }}
                    className="mt-3 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-slate-900 px-3 text-center text-sm font-semibold text-white transition-colors hover:bg-slate-700"
                  >
                    Открыть разбор →
                  </a>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-500">
                    <button
                      type="button"
                      onClick={() => pickSample(pair)}
                      disabled={pickingSample !== null}
                      className="inline-flex min-h-[36px] items-center whitespace-nowrap underline hover:text-slate-800 disabled:opacity-50"
                    >
                      {pickingSample === pair.id ? 'подставляем…' : 'подставить в форму'}
                    </button>
                    <a
                      className="inline-flex min-h-[36px] items-center whitespace-nowrap underline hover:text-slate-800"
                      href={`/api/samples/${pair.spec}`}
                      download
                    >
                      скачать ТЗ
                    </a>
                    <a
                      className="inline-flex min-h-[36px] items-center whitespace-nowrap underline hover:text-slate-800"
                      href={`/api/samples/${pair.price}`}
                      download
                    >
                      скачать прайс
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Permalink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* буфер недоступен — ссылка всё равно видна и выделяется */
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-3 sm:px-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="basis-full text-xs font-semibold uppercase tracking-wide text-slate-500 sm:basis-auto sm:text-sm sm:font-normal sm:normal-case sm:tracking-normal">
          Постоянная ссылка на этот разбор
        </span>
        <a
          href={url}
          className="min-w-0 flex-1 basis-full break-anywhere py-2 font-mono text-[13px] leading-snug text-slate-900 underline decoration-slate-300 underline-offset-2 sm:basis-auto"
        >
          {url}
        </a>
        <button
          type="button"
          onClick={copy}
          className="inline-flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white px-3.5 text-[13px] font-medium text-slate-700 transition-colors hover:bg-slate-100 sm:ml-auto sm:min-h-0 sm:w-auto sm:py-1.5"
        >
          {copied ? 'скопировано' : 'копировать'}
        </button>
      </div>
    </div>
  );
}

// Название примера может нести предупреждение через «⚠️ …» — выносим его
// в отдельную заметку, чтобы шапка не превращалась в три строки мелкого текста.
function splitWarning(title: string): { title: string; warning: string } {
  const i = title.indexOf('⚠️');
  if (i < 0) return { title: title.trim(), warning: '' };
  return {
    title: title.slice(0, i).replace(/[\s×·—–-]+$/u, '').trim(),
    warning: title.slice(i + '⚠️'.length).trim(),
  };
}

// Длинные тексты (в ТЗ из docx попадаются абзацы договора на 600+ знаков)
// сворачиваем до нескольких строк с явной кнопкой-раскрытием.
function ClampedText({
  text, className = '', clampClass = 'line-clamp-3', threshold = 160,
}: { text: string; className?: string; clampClass?: string; threshold?: number }) {
  const [open, setOpen] = useState(false);
  const long = text.length > threshold;
  return (
    <div className={className}>
      <div className={!long || open ? 'break-anywhere' : `break-anywhere ${clampClass}`}>{text}</div>
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="mt-1 inline-flex min-h-[32px] items-center text-xs font-medium text-slate-500 underline underline-offset-2 hover:text-slate-800"
        >
          {open ? 'свернуть' : 'показать полностью'}
        </button>
      )}
    </div>
  );
}

function CategoryBadge({ category }: { category: Position['category'] }) {
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${CAT_BADGE[category]}`}
    >
      {CAT_LABEL[category]}
    </span>
  );
}

// Допустимость: заголовок + основания. Оснований бывает до шести, каждое —
// до ~170 знаков, поэтому показываем два, остальные под кнопкой.
function AdmissibilityBlock({ adm, className = '' }: { adm: Admissibility; className?: string }) {
  const [open, setOpen] = useState(false);
  const grounds = adm.grounds || [];
  const shown = open ? grounds : grounds.slice(0, 2);
  const rest = grounds.length - shown.length;
  return (
    <div
      className={`rounded-lg border px-2.5 py-2 text-xs leading-snug ${ADM_CHIP[adm.status] || ADM_CHIP.undetermined} ${className}`}
    >
      <div className="font-semibold break-anywhere">{adm.status_ru}</div>
      {shown.length > 0 && (
        <ul className="mt-1 space-y-0.5 opacity-90">
          {shown.map((g, i) => (
            <li key={i} className="flex gap-1.5">
              <span aria-hidden className="shrink-0">·</span>
              <span className="min-w-0 break-anywhere">{g}</span>
            </li>
          ))}
        </ul>
      )}
      {rest > 0 && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-1 inline-flex min-h-[36px] items-center font-medium underline underline-offset-2"
        >
          ещё {rest}
        </button>
      )}
      {open && grounds.length > 2 && (
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="mt-1 inline-flex min-h-[36px] items-center font-medium underline underline-offset-2"
        >
          свернуть
        </button>
      )}
    </div>
  );
}

const qtyLabel = (p: Position) => (p.qty != null ? `${p.qty} ${p.unit}`.trim() : '—');

// Мобильная карточка позиции: всё, что на десктопе лежит в семи колонках,
// читается сверху вниз без горизонтального скролла.
function PositionCard({ p }: { p: Position }) {
  return (
    <article className="rounded-xl border border-slate-200 p-3.5">
      <div className="flex items-start gap-2.5">
        <span className="w-6 shrink-0 pt-0.5 text-right text-xs font-semibold tabular-nums text-slate-400">
          {p.num}
        </span>
        <div className="min-w-0 flex-1">
          <ClampedText text={p.name} className="text-[15px] font-medium leading-snug text-slate-900" />
          <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-2">
            <CategoryBadge category={p.category} />
            {p.qty != null && (
              <span className="whitespace-nowrap text-sm text-slate-600">{qtyLabel(p)}</span>
            )}
          </div>
        </div>
      </div>

      {p.found ? (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Найдено в прайсе
          </div>
          <div className="mt-1 break-anywhere text-sm leading-snug text-slate-900">{p.found.name}</div>
          {p.found.code && (
            <div className="mt-1 break-anywhere font-mono text-xs text-slate-400">{p.found.code}</div>
          )}
          {(p.found.price != null || p.found.sum != null) && (
            <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              {p.found.price != null && (
                <span className="whitespace-nowrap text-sm text-slate-500">
                  {fmtMoney(p.found.price)} ₽/ед.
                </span>
              )}
              {p.found.sum != null && (
                <span className="whitespace-nowrap text-base font-semibold tabular-nums text-slate-900">
                  {fmtMoney(p.found.sum)} ₽
                </span>
              )}
            </div>
          )}
        </div>
      ) : p.category !== 'none' ? (
        // Для категории «нет в прайсе» бейдж уже всё сказал — плашку не дублируем.
        <div className="mt-3 rounded-lg border border-dashed border-slate-200 px-3 py-2 text-sm text-slate-400">
          В прайсе не нашлось
        </div>
      ) : null}

      {p.explanation && (
        <ClampedText
          text={p.explanation}
          threshold={200}
          clampClass="line-clamp-4"
          className="mt-2.5 text-sm leading-snug text-slate-500"
        />
      )}

      {p.admissibility && <AdmissibilityBlock adm={p.admissibility} className="mt-2.5" />}
    </article>
  );
}

function PositionRow({ p }: { p: Position }) {
  return (
    <tr className="border-b border-slate-100 align-top">
      <td className="px-2 py-3 text-xs tabular-nums text-slate-400">{p.num}</td>
      <td className="px-2 py-3">
        <ClampedText text={p.name} className="break-anywhere leading-snug text-slate-900" />
        {p.explanation && (
          <ClampedText
            text={p.explanation}
            threshold={200}
            clampClass="line-clamp-3"
            className="mt-1 text-xs leading-snug text-slate-500"
          />
        )}
      </td>
      <td className="px-2 py-3 text-slate-700">
        <span className="whitespace-nowrap">{qtyLabel(p)}</span>
      </td>
      <td className="px-2 py-3">
        <CategoryBadge category={p.category} />
      </td>
      <td className="px-2 py-3">
        {p.found ? (
          <div>
            <div className="break-anywhere leading-snug text-slate-900">{p.found.name}</div>
            <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-slate-500">
              {p.found.code && <span className="break-anywhere font-mono">{p.found.code}</span>}
              {p.found.price != null && (
                <span className="whitespace-nowrap">{fmtMoney(p.found.price)} ₽/ед.</span>
              )}
            </div>
          </div>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="px-2 py-3 text-right tabular-nums text-slate-900">
        <span className="whitespace-nowrap">{p.found?.sum != null ? fmtMoney(p.found.sum) : ''}</span>
      </td>
      <td className="px-2 py-3">
        {p.admissibility && <AdmissibilityBlock adm={p.admissibility} />}
      </td>
    </tr>
  );
}

function RazborResult({
  data, downloadUrl, permalink, onReset,
}: { data: RazborResponse; downloadUrl: string; permalink: string | null; onReset: () => void }) {
  const c = data.coverage;
  const verdictStyle =
    c.verdict === 'идём'
      ? 'bg-emerald-600'
      : c.verdict === 'частично'
        ? 'bg-amber-500'
        : 'bg-rose-600';
  const seg = (n: number) => (c.total ? (100 * n) / c.total : 0);
  const pair = data.pair ? splitWarning(data.pair.title) : null;

  return (
    <div className="space-y-5 sm:space-y-6">
      {pair && (
        <div className="space-y-2">
          <div className="break-anywhere text-sm leading-snug text-slate-500">
            Пример: <span className="font-medium text-slate-900">{pair.title}</span>
          </div>
          {pair.warning && (
            <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] leading-snug text-amber-900">
              <span aria-hidden className="shrink-0">⚠️</span>
              <span className="min-w-0 break-anywhere">{pair.warning}</span>
            </div>
          )}
        </div>
      )}

      {/* Сводка: процент + вердикт + действия */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="text-4xl font-bold leading-none tabular-nums text-slate-900 sm:text-5xl">
            {c.percent}%
          </div>
          <span
            className={`${verdictStyle} whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-white sm:text-sm`}
          >
            {c.verdict}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <a
            href={downloadUrl}
            download={`разбор_${data.spec_name.replace(/\.[^.]+$/, '')}.xlsx`}
            className="inline-flex min-h-[44px] flex-1 items-center justify-center whitespace-nowrap rounded-lg bg-slate-900 px-4 text-sm font-semibold text-white transition-colors hover:bg-slate-700 sm:flex-none sm:px-5"
          >
            Скачать разбор (xlsx)
          </a>
          <button
            onClick={onReset}
            className="inline-flex min-h-[44px] shrink-0 items-center whitespace-nowrap px-1 text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            Новый разбор
          </button>
        </div>
      </div>

      <div className="break-anywhere text-[13px] leading-snug text-slate-500 sm:text-sm">
        {data.spec_name} · {c.total} позиций
        <span aria-hidden className="mx-1.5 text-slate-300">→</span>
        {data.price_name} · {data.n_catalog} строк
      </div>

      {/* Полоса покрытия */}
      <div>
        <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
          <div className="bg-emerald-500" style={{ width: `${seg(c.exact)}%` }} />
          <div className="bg-sky-500" style={{ width: `${seg(c.analog)}%` }} />
          <div className="bg-amber-400" style={{ width: `${seg(c.review + c.forbidden)}%` }} />
          <div className="bg-orange-300" style={{ width: `${seg(c.none)}%` }} />
        </div>
        <div className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-1.5 text-[13px] text-slate-600 sm:flex sm:flex-wrap sm:gap-x-5 sm:gap-y-1 sm:text-xs">
          <Legend color="bg-emerald-500" label={`точно · ${c.exact}`} />
          <Legend color="bg-sky-500" label={`аналог · ${c.analog}`} />
          <Legend color="bg-amber-400" label={`спорно · ${c.review + c.forbidden}`} />
          <Legend color="bg-orange-300" label={`нет в прайсе · ${c.none}`} />
        </div>
      </div>

      {/* Деньги + блокеры */}
      <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Сумма покрытых позиций
          </div>
          <div className="mt-1 break-anywhere text-2xl font-bold tabular-nums text-slate-900">
            {fmtMoney(data.money.total)} ₽
          </div>
          <div className="mt-1 text-[13px] leading-snug text-slate-500">
            цена и количество известны для {data.money.known_rows} позиций
          </div>
        </div>
        {c.blockers.length > 0 && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Блокеры</div>
            <ul className="mt-1.5 space-y-1 text-[13px] leading-snug text-amber-900 sm:text-sm">
              {c.blockers.slice(0, 4).map((b, i) => (
                <li key={i} className="flex gap-1.5">
                  <span aria-hidden className="shrink-0">•</span>
                  <span className="min-w-0 break-anywhere">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Ссылка на разбор — под сводкой, чтобы цифры помещались на первый экран телефона */}
      {permalink && <Permalink url={permalink} />}

      {/* Позиции: карточки на телефоне, таблица от lg */}
      <div data-testid="razbor-positions">
        <div className="mb-3 flex items-baseline gap-2 border-t border-slate-200 pt-5">
          <h2 className="text-base font-semibold text-slate-900">Позиции</h2>
          <span className="text-sm text-slate-500">{data.positions.length}</span>
        </div>

        <div className="space-y-3 lg:hidden">
          {data.positions.map((p) => (
            <PositionCard key={p.num} p={p} />
          ))}
        </div>

        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full table-fixed text-sm">
            <colgroup>
              <col className="w-[4%]" />
              <col className="w-[26%]" />
              <col className="w-[8%]" />
              <col className="w-[12%]" />
              <col className="w-[24%]" />
              <col className="w-[9%]" />
              <col className="w-[17%]" />
            </colgroup>
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-2 py-2 font-semibold">#</th>
                <th className="px-2 py-2 font-semibold">Позиция из спецификации</th>
                <th className="px-2 py-2 font-semibold">Кол-во</th>
                <th className="px-2 py-2 font-semibold">Категория</th>
                <th className="px-2 py-2 font-semibold">Найдено в прайсе</th>
                <th className="px-2 py-2 text-right font-semibold">Сумма, ₽</th>
                <th className="px-2 py-2 font-semibold">Допустимость</th>
              </tr>
            </thead>
            <tbody>
              {data.positions.map((p) => (
                <PositionRow key={p.num} p={p} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${color}`} />
      {label}
    </span>
  );
}
